import { View } from "dhx-optimus";
import type { Grid } from "@lib/suite/suite";
import type { IRow } from "@root/lib/suite/types/ts-grid";
import { IAppState, RowButton } from "@root/types";

import "./HospitalTable.scss";

class HospitalTable extends View<IAppState> {
	table: Grid;

	init() {
		this.table = new dhx.Grid(null, {
			type: "tree",
			columns: [
				{
					id: "bedNumber",
					header: [{ text: "Bed number" }],
					align: "left",
					minWidth: 160,
				},
				{
					id: "status",
					header: [{ text: "Status" }],
					align: "center",
					minWidth: 160,
					mark: () => "dhx-demo_cell-template",
					template: (value: string) => {
						if (!value) return;
						let css = "dhx-demo_status-template";
						switch (value) {
							case "available":
								css += "--available";
								break;
							case "occupied":
							default:
								css += "--occupied";
								break;
						}
						return `
							<div class = "dhx-demo_status-wrapper">
								<div class="dhx-demo_status-template">
									<span class="${css}">${value}</span>
								</div>
							</div>
						`;
					},
					htmlEnable: true,
					options: ["available", "occupied"],
					editorType: "select",
				},
				{
					id: "patientName",
					header: [{ text: "Patient name" }],
					align: "left",
					minWidth: 170,
				},
				{
					id: "diagnosis",
					header: [{ text: "Diagnosis" }],
					align: "left",
					minWidth: 170,
				},
				{
					id: "patientHeight",
					header: [{ text: "Height (m)" }],
					type: "number",
					align: "left",
					minWidth: 160,
					numberMask: {
						suffix: " m",
						groupSeparator: "",
						decSeparator: ".",
						allowNegative: false,
						maxIntLength: 1,
						maxDecLength: 2,
						minDecLength: 2,
					},
				},
				{
					id: "patientWeight",
					header: [{ text: "Weight (kg)" }],
					type: "number",
					align: "left",
					minWidth: 160,
					numberMask: {
						suffix: " kg",
						groupSeparator: "",
						decSeparator: ".",
						allowNegative: false,
						maxIntLength: 3,
						maxDecLength: 1,
						minDecLength: 0,
					},
				},
				{
					id: "patientTemperature",
					header: [{ text: "Temperature (°C)" }],
					type: "number",
					align: "left",
					minWidth: 160,
					numberMask: {
						suffix: " °C",
						groupSeparator: "",
						decSeparator: ".",
						allowNegative: false,
						maxIntLength: 2,
						maxDecLength: 1,
						minDecLength: 1,
					},
				},
				{
					id: "bloodPressure",
					header: [{ text: "Blood pressure (mmHg)" }],
					align: "left",
					minWidth: 170,
					patternMask: {
						pattern: (value: string | number) => {
							const str = String(value).replace(/[^0-9/]/g, "");
							const parts = str.split("/");
							if (parts.length === 2) {
								return "#".repeat(parts[0].length) + "/" + "#".repeat(parts[1].length);
							}
							return "###/##";
						},
						charFormat: {
							"#": /[0-9]/,
						},
					},
				},
				{
					id: "careRequired",
					header: [{ text: "Care required" }],
					align: "left",
					minWidth: 170,
				},
				{
					id: "doctor",
					header: [{ text: "Doctor assigned" }],
					align: "left",
					minWidth: 170,
				},
				{
					id: "admission",
					header: [{ text: "Admission" }],
					type: "date",
					align: "left",
					minWidth: 160,
					dateFormat: "%m/%d/%Y",
					editorType: "datePicker",
				},
				{
					id: "controls",
					header: [{ text: "Controls" }],
					align: "center",
					minWidth: 170,
					editable: false,
					mark: () => "dhx-demo_cell-template",
					template: (value: RowButton[]) => {
						if (!value) return;
						const controls = value.map((item: RowButton) => {
							const icon = item === "edit" ? "mdi mdi-pencil" : "mdi mdi-delete-forever";
							return `
								<button class="dhx-demo_controls-template__button ${item}">
									<i class="${icon} dhx-demo_controls-template__icon"></i>
								</button>
							`;
						});
						return `
							<div class="dhx-demo_controls-template dhx-demo_control--${value}">
								${controls.join("")}
							</div>
						`;
					},
					htmlEnable: true,
				},
			],
			eventHandlers: {
				onclick: {
					clear: (_event: MouseEvent, { row }: IRow) => {
						this.fire("clearPatientData", [row.id]);
					},
					edit: (_event: MouseEvent, { row }: IRow) => {
						this.fire("openPaientCard", [row.id]);
					},
				},
			},
			data: this.params.hospital,
			selection: "complex",
			editable: true,
		});

		return this.table;
	}

	ready() {
		const rowId = this.table.data.getId(0);
		const colId = this.table.config.columns[0].id;
		this.table.selection.setCell(rowId, colId);
	}
}

export default HospitalTable;
